import React, { useState } from 'react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import {
  ShoppingBagIcon, CurrencyRupeeIcon, UsersIcon,
  BuildingStorefrontIcon, ArrowUturnLeftIcon, ChartBarIcon,
} from '@heroicons/react/24/outline';
import { useQuery } from '../../hooks';
import { adminAPI } from '../../services/api';
import { StatCard, LoadingPage, Card, Badge, StatusBadge } from '../../components/common/UI';
import { format } from 'date-fns';

const PIE_COLORS = ['#6366f1','#22c55e','#f59e0b','#ef4444','#8b5cf6','#3b82f6'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-surface-100 bg-white p-3 shadow-card-lg text-xs">
      <p className="font-semibold text-surface-700 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-medium">
          {p.name}: {p.name === 'revenue' ? `₹${Number(p.value).toLocaleString()}` : p.value}
        </p>
      ))}
    </div>
  );
};

export default function AdminDashboard() {
  const [range] = useState('30d');
  const { data, loading } = useQuery(() => adminAPI.dashboard(), []);

  if (loading) return <LoadingPage />;

  const summary   = data?.summary || {};
  const byStatus  = data?.orders_by_status || [];
  const byDay     = (data?.revenue_by_day  || []).slice().reverse();

  return (
    <div className="space-y-6 animate-in">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="mt-0.5 text-sm text-surface-500">
            {format(new Date(), 'EEEE, dd MMMM yyyy')}
          </p>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4 xl:grid-cols-5">
        <StatCard label="Total Orders"     value={summary.total_orders}      icon={ShoppingBagIcon}          color="blue"   change={12} />
        <StatCard label="Revenue"          value={`₹${Number(summary.total_revenue||0).toLocaleString()}`} icon={CurrencyRupeeIcon} color="green"  change={8} />
        <StatCard label="Customers"        value={summary.total_customers}    icon={UsersIcon}                color="purple" change={5} />
        <StatCard label="Businesses"       value={summary.active_businesses}  icon={BuildingStorefrontIcon}   color="orange" />
        <StatCard label="Pending Returns"  value={summary.pending_returns}    icon={ArrowUturnLeftIcon}       color="red" />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Revenue area chart */}
        <Card className="lg:col-span-2">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="font-display font-semibold text-surface-900">Revenue Trend</h2>
            <Badge variant="purple">Last 30 days</Badge>
          </div>
          {byDay.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={byDay} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#6366f1" stopOpacity={0.18} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={d => format(new Date(d), 'dd MMM')} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="revenue" name="revenue" stroke="#6366f1" strokeWidth={2} fill="url(#revGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-[220px] items-center justify-center text-sm text-surface-400">No data yet</div>
          )}
        </Card>

        {/* Orders by status pie */}
        <Card>
          <h2 className="mb-5 font-display font-semibold text-surface-900">Orders by Status</h2>
          {byStatus.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={byStatus} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={75} paddingAngle={3}>
                  {byStatus.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [v, n?.replace(/_/g,' ') ?? n]} />
                <Legend formatter={v => v?.replace(/_/g,' ') ?? v} iconSize={8} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-[220px] items-center justify-center text-sm text-surface-400">No data</div>
          )}
        </Card>
      </div>

      {/* Orders bar */}
      <Card>
        <h2 className="mb-5 font-display font-semibold text-surface-900">Daily Orders</h2>
        {byDay.length ? (
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={byDay} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={d => format(new Date(d), 'dd')} />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="orders" fill="#6366f1" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-[160px] items-center justify-center text-sm text-surface-400">No data</div>
        )}
      </Card>
    </div>
  );
}
